import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ItemStatusBadge } from '../shared/StatusBadge';
import type { InventoryItem } from '../../types';

interface LowStockAlertsProps {
  items: InventoryItem[];
  isLoading?: boolean;
}

export function LowStockAlerts({ items, isLoading }: LowStockAlertsProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Low Stock Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Low Stock Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No low stock items. All inventory levels are healthy.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Low Stock Alerts
            <Badge variant="outline" className="ml-2 text-orange-500 border-orange-500">
              {items.length}
            </Badge>
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/wms/inventory?lowStock=true">View All</Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {items.slice(0, 5).map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-medium truncate">
                    {item.sku_code}
                  </span>
                  <ItemStatusBadge status={item.item_status} />
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {item.sku_name || item.sku_details?.name?.en || 'Unknown'}
                </p>
              </div>
              <div className="text-right ml-4">
                <div className="text-sm font-medium text-orange-500">
                  {item.quantity_available}
                </div>
                <p className="text-xs text-muted-foreground">
                  / {item.reorder_point || 0} min
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
